import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

const PageContainer = styled.div`
  padding-top: 120px; /* Account for standard fixed navbar */
  min-height: 100vh;
  background: ${theme.colors.background};
`;

const PageHero = styled.section`
  background: linear-gradient(135deg, rgba(40,82,111,0.9), rgba(64,120,170,0.8)),
              url('/img_to_use/about.jpg') center/cover no-repeat;
  padding: 4rem 0;
  text-align: center;
  color: white;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  opacity: 0.95;
  max-width: 600px;
  margin: 0 auto;
`;

const ContentSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  padding: 4rem 2rem;
  line-height: 1.8;
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  color: ${theme.colors.primary};
  margin-bottom: 1.5rem;
  margin-top: 3rem;
  
  &:first-child {
    margin-top: 0;
  }
`;

const SubsectionTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  color: ${theme.colors.text};
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  color: ${theme.colors.text};
  font-size: 1rem;
`;

const BulletList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 2rem;
  
  li {
    margin-bottom: 0.5rem;
    color: ${theme.colors.text};
  }
`;

const ContactInfo = styled.div`
  background: ${theme.colors.sectionAlt};
  padding: 2rem;
  border-radius: 12px;
  border-left: 4px solid ${theme.colors.primary};
  margin: 2rem 0;
  
  h4 {
    font-family: 'Playfair Display', serif;
    color: ${theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
  }
`;

const LastUpdated = styled.div`
  font-style: italic;
  color: ${theme.colors.text};
  opacity: 0.7;
  margin-bottom: 2rem;
  text-align: center;
`;

const TermsConditionsPage: React.FC = () => {
  return (
    <PageContainer>
      <PageHero>
        <HeroContent>
          <PageTitle>Terms & Conditions</PageTitle>
          <PageSubtitle>
            Please read these terms and conditions carefully before using our services
          </PageSubtitle>
        </HeroContent>
      </PageHero>
      
      <ContentSection>
        <LastUpdated>
          Last updated: {new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </LastUpdated>

        <SectionTitle>1. Acceptance of Terms</SectionTitle>
        <Paragraph>
          By using De ARC Global LLC services, you agree to these terms. If you disagree, please do not use our services.
        </Paragraph>

        <SectionTitle>2. Use License</SectionTitle>
        <Paragraph>
          You may download materials for personal, non-commercial viewing only. You may not modify, copy for commercial use, reverse engineer, or remove copyright notices.
        </Paragraph>

        <SectionTitle>3. SMS/Text Messaging Services</SectionTitle>
        
        <SubsectionTitle>3.1 SMS Consent</SubsectionTitle>
        <Paragraph>
          By providing your phone number, you consent to receive text messages including appointment reminders, service updates, job alerts, and business communications.
        </Paragraph>

        <SubsectionTitle>3.2 Rates & Frequency</SubsectionTitle>
        <Paragraph>
          Message frequency varies. Standard message and data rates apply. You are responsible for all charges.
        </Paragraph>

        <SubsectionTitle>3.3 Supported Carriers</SubsectionTitle>
        <Paragraph>
          Our SMS services are supported by major U.S. mobile carriers. For questions about specific carrier support or message delivery issues, please contact us at the information provided below.
        </Paragraph>

        <SubsectionTitle>3.4 Opt-Out Instructions</SubsectionTitle>
        <Paragraph>
          You may opt out of receiving text messages at any time by:
        </Paragraph>
        <BulletList>
          <li>Replying "STOP" to any text message</li>
          <li>Texting "UNSUBSCRIBE" to our number</li>
          <li>Contacting us directly using the contact information provided below</li>
        </BulletList>
        <Paragraph>
          After opting out, you will receive a confirmation message, and no further messages will be sent unless you opt in again.
        </Paragraph>

        <SubsectionTitle>3.5 Help and Support</SubsectionTitle>
        <Paragraph>
          For help with our text messaging services, reply "HELP" to any message or contact us using the information provided below.
        </Paragraph>

        <SectionTitle>4. Privacy and Data Protection</SectionTitle>
        <Paragraph>
          Your privacy is important to us. Our collection and use of personal information in connection with our SMS services is governed by our Privacy Policy. We will not share your mobile phone number or SMS opt-in status with third parties for marketing purposes unrelated to our services.
        </Paragraph>

        <SectionTitle>5. Service Availability</SectionTitle>
        <Paragraph>
          While we strive to provide reliable SMS services, message delivery is subject to carrier networks and may be affected by factors beyond our control. We do not guarantee the delivery, timeliness, or accuracy of any message.
        </Paragraph>

        <SectionTitle>6. Disclaimer</SectionTitle>
        <Paragraph>
          The materials on De ARC Global LLC's website are provided on an 'as is' basis. De ARC Global LLC makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
        </Paragraph>

        <SectionTitle>7. Limitations</SectionTitle>
        <Paragraph>
          In no event shall De ARC Global LLC or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on De ARC Global LLC's website, even if De ARC Global LLC or a De ARC Global LLC authorized representative has been notified orally or in writing of the possibility of such damage. Because some jurisdictions do not allow limitations on implied warranties, or limitations of liability for consequential or incidental damages, these limitations may not apply to you.
        </Paragraph>

        <SectionTitle>8. Accuracy of Materials</SectionTitle>
        <Paragraph>
          The materials appearing on De ARC Global LLC's website could include technical, typographical, or photographic errors. De ARC Global LLC does not warrant that any of the materials on its website are accurate, complete, or current. De ARC Global LLC may make changes to the materials contained on its website at any time without notice. However, De ARC Global LLC does not make any commitment to update the materials.
        </Paragraph>

        <SectionTitle>9. Links</SectionTitle>
        <Paragraph>
          De ARC Global LLC has not reviewed all of the sites linked to our website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by De ARC Global LLC of the site. Use of any such linked website is at the user's own risk.
        </Paragraph>

        <SectionTitle>10. Modifications</SectionTitle>
        <Paragraph>
          De ARC Global LLC may revise these terms of service for its website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.
        </Paragraph>

        <SectionTitle>11. Governing Law</SectionTitle>
        <Paragraph>
          These terms and conditions are governed by and construed in accordance with the laws of Michigan, United States, and you irrevocably submit to the exclusive jurisdiction of the courts in that state or location.
        </Paragraph>

        <ContactInfo>
          <h4>Contact Information</h4>
          <p><strong>De ARC Global LLC</strong></p>
          <p>Canton, Michigan 48186</p>
          <p>United States</p>
          <p>Phone: +1 734-560-2886</p>
          <p>Email: info@dearcglobal.com</p>
        </ContactInfo>
      </ContentSection>
    </PageContainer>
  );
};

export default TermsConditionsPage;
